import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  countryCitiesSelector,
} from 'redux/selectors/regionSelectors';
import { BillingInfoFormValues, BillingInfoMainLocation } from '../BillingInfo';
import { useDidUpdate } from 'rooks';

type Props = {
  formik: FormikProps<BillingInfoFormValues>;
  mainLocation: BillingInfoMainLocation;
};

export default function InternationalBillingInfoStateForm(props: Props) {
  const { formik, mainLocation } = props;
  const locationSameAsMainValue =
    formik.values.billingLocation.locationSameAsMain;

  const [selectedCountry, setSelectedCountry] = useState<number | undefined>(
    locationSameAsMainValue
      ? mainLocation.country
      : formik.values.billingLocation.country,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const cities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);

  const filteredCities = useMemo(
    () => cities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, cities],
  );

  const updateSelectedValue = (
    setter: React.Dispatch<React.SetStateAction<number | undefined>>,
    mainValue?: number,
    billingValue?: number,
  ) => {
    setter(locationSameAsMainValue ? mainValue : billingValue);
  };

  useDidUpdate(() => {
    updateSelectedValue(
      setSelectedCountry,
      mainLocation.country,
      formik.values.billingLocation.country,
    );
  }, [mainLocation.country, formik.values.billingLocation.country]);

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<BillingInfoFormValues>,
  ) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('billingLocation.city', '', false);
    formik.setFieldValue('billingLocation.state', '', false);
    formik.setFieldValue('billingLocation.internationalCityName', '', false);
    formik.setFieldValue('billingLocation.internationalStateName', '', false);
  };

  return (
    <>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="billingLocation.country"
              label="Country *"
              data-id="billingInfoCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                name="billingLocation.country"
                showSearch
                disabled={locationSameAsMainValue}
                placeholder="Select country"
                optionFilterProp="children"
                onChange={(value: number) => handleCountryChange(value, formik)}
                value={
                  locationSameAsMainValue
                    ? mainLocation.country
                    : selectedCountry
                }
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="billingLocation.internationalStateName"
              label="State *"
              data-id="billingInfoStateField"
            >
              <Input
                name="billingLocation.internationalStateName"
                placeholder="Enter state"
                disabled={
                  !formik.values.billingLocation.country ||
                  locationSameAsMainValue
                }
                value={
                  (locationSameAsMainValue
                    ? mainLocation.internationalStateName
                    : formik.values.billingLocation.internationalStateName) ||
                  undefined
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="ZIP code"
              data-id="newClientBillingZipField"
              name="billingLocation.zip"
            >
              <Input
                name="billingLocation.zip"
                className="full-width"
                placeholder="Enter ZIP code"
                disabled={locationSameAsMainValue}
                value={
                  locationSameAsMainValue
                    ? mainLocation.zip
                    : formik.values.billingLocation.zip
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              data-id="companyCityField"
              name="billingLocation.internationalCityName"
              label="City *"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name="billingLocation.internationalCityName"
                filterOption
                disabled={
                  !formik.values.billingLocation.internationalStateName ||
                  locationSameAsMainValue
                }
                placeholder="Enter city"
                showSearch
                value={
                  locationSameAsMainValue
                    ? mainLocation.internationalCityName
                    : formik.values.billingLocation.internationalCityName
                }
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`billing-info-city-state-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              label="Address *"
              data-id="newClientBillingAddressField"
              name="billingLocation.address"
            >
              <Input
                name="billingLocation.address"
                placeholder="Enter address"
                disabled={locationSameAsMainValue}
                value={
                  locationSameAsMainValue
                    ? mainLocation.address
                    : formik.values.billingLocation.address
                }
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </>
  );
}
